import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { RealtimeClient } from '@speechmatics/real-time-client';
import { appState } from "@/app/appState";
import { deserializeProfiles } from "../utils/speakerProfileSerializer";

interface SpeakerProfileRecorderProps {
    /** Called with the captured speaker profile identifier string when successfully generated */
    onProfileCaptured: (profile: string[]) => void;
    /** Existing profile identifier — used to show state and button label */
    existingProfile?: string[] | null | string;
    /** Optional label shown in the "active profile" line */
    label?: string;
    /** The specific audio device this profile is being recorded for */
    deviceLabel?: string;
    isPopup?: boolean;
}

export const SpeakerProfileRecorder = observer(({
    onProfileCaptured,
    existingProfile,
    label = "voice profile",
    deviceLabel = "current microphone",
    isPopup = false,
}: SpeakerProfileRecorderProps) => {
    const displayLabel = label === "voice profile" ? appState.t("speaker.voiceProfile") : label;
    const [isRecording, setIsRecording] = useState(false);
    const [statusText, setStatusText] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [transcript, setTranscript] = useState("");
    const [audioLevel, setAudioLevel] = useState<number>(0);
    const [micPermission, setMicPermission] = useState<boolean | null>(null);

    const clientRef = useRef<RealtimeClient | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const inputRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const transcriptRef = useRef("");
    const animationFrameRef = useRef<number | null>(null);
    const processorRef = useRef<AudioWorkletNode | null>(null);

    const cleanup = useCallback(() => {
        if (clientRef.current) clientRef.current = null;
        if (processorRef.current) {
            processorRef.current.disconnect();
            processorRef.current = null;
        }
        if (inputRef.current) {
            inputRef.current.disconnect();
            inputRef.current = null;
        }
        if (analyserRef.current) {
            analyserRef.current.disconnect();
            analyserRef.current = null;
        }
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(t => t.stop());
            mediaStreamRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close().catch(() => { });
            audioContextRef.current = null;
        }
    }, []);

    const stopRecording = useCallback(() => {
        setIsRecording(false);
        setStatusText(appState.t("speaker.transcribing"));
        if (processorRef.current) processorRef.current.disconnect();
        if (inputRef.current) inputRef.current.disconnect();
        if (clientRef.current) {
            clientRef.current.stopRecognition().catch(e => console.warn(e));
        }
    }, []);

    const updateAudioLevel = () => {
        if (analyserRef.current) {
            const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
            analyserRef.current.getByteFrequencyData(dataArray);
            let sum = 0;
            for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
            setAudioLevel((sum / dataArray.length) / 2.55);
            animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
        }
    };

    useEffect(() => {
        if (isRecording) {
            updateAudioLevel();
        } else {
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
            setAudioLevel(0);
        }
        return () => {
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        };
    }, [isRecording]);

    useEffect(() => {
        navigator.mediaDevices.getUserMedia({ audio: true })
            .then((stream) => {
                setMicPermission(true);
                stream.getTracks().forEach(track => track.stop());
            })
            .catch((err) => {
                console.error("Mic check failed", err);
                setMicPermission(false);
                setError(appState.t("speaker.micDenied"));
            });
    }, []);

    const startRecording = useCallback(async () => {
        setError(null);
        setStatusText(appState.t("speaker.connecting"));
        setTranscript("");
        transcriptRef.current = "";

        try {
            const tokenRes = await fetch('/api/transcription/get_atot_token');
            if (!tokenRes.ok) throw new Error(appState.t("speaker.failedToken"));
            const { token } = await tokenRes.json();

            const audioContext = new AudioContext({ latencyHint: 'balanced' });
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: audioContext.sampleRate,
                    channelCount: 1,
                    // Must match TranscriptionComponent so profiles are captured with the same processing.
                    noiseSuppression: false
                }
            });
            mediaStreamRef.current = stream;
            audioContextRef.current = audioContext;

            const source = audioContext.createMediaStreamSource(stream);
            inputRef.current = source;

            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 64;
            source.connect(analyser);
            analyserRef.current = analyser;

            const client = new RealtimeClient({ url: "wss://eu.rt.speechmatics.com/v2" });
            clientRef.current = client;

            client.addEventListener('receiveMessage', ({ data }) => {
                const message = data as any;
                if (message.message === 'AddTranscript') {
                    if (message.metadata?.transcript) {
                        transcriptRef.current += " " + message.metadata.transcript;
                        const currentText = transcriptRef.current.trim();
                        setTranscript(currentText);
                        if (currentText.split(' ').length >= 6) stopRecording();
                    }
                } else if (message.message === 'AddPartialTranscript') {
                    if (message.metadata?.transcript) {
                        setTranscript(transcriptRef.current + " " + message.metadata.transcript);
                    }
                } else if (message.message === 'SpeakersResult') {
                    if (message.speakers?.length > 0) {
                        const speakersWithId = message.speakers.filter(
                            (s: any) => s.speaker_identifiers?.length > 0
                        );
                        if (speakersWithId.length > 0) {
                            const profile: string[] = speakersWithId[0].speaker_identifiers;
                            onProfileCaptured(profile);
                            setStatusText(appState.t("speaker.saved"));
                        } else {
                            setError("No distinct voice identified. Try speaking longer.");
                        }
                    }
                } else if (message.message === 'EndOfTranscript') {
                    cleanup();
                    setIsRecording(false);
                    setStatusText(prev => prev === appState.t("speaker.saved") ? prev : "Session closed.");
                } else if (message.message === 'Error') {
                    setError(`Error: ${message.reason}`);
                    cleanup();
                    setIsRecording(false);
                }
            });

            await client.start(token, {
                audio_format: { type: 'raw', encoding: 'pcm_f32le', sample_rate: audioContext.sampleRate },
                transcription_config: {
                    language: "da",
                    diarization: "speaker",
                    operating_point: 'enhanced',
                    speaker_diarization_config: {
                        max_speakers: 2,
                        get_speakers: true,
                        speaker_sensitivity: 0.5,
                    }
                }
            });

            setIsRecording(true);
            setStatusText(appState.t("speaker.recordingHint"));

            const workletCode = `
                class PCMProcessor extends AudioWorkletProcessor {
                    process(inputs) {
                        const input = inputs[0];
                        if (input && input[0] && input[0].length > 0) {
                            this.port.postMessage(input[0]);
                        }
                        return true;
                    }
                }
                registerProcessor('pcm-processor', PCMProcessor);
            `;
            const blob = new Blob([workletCode], { type: 'application/javascript' });
            const workletUrl = URL.createObjectURL(blob);
            await audioContext.audioWorklet.addModule(workletUrl);
            URL.revokeObjectURL(workletUrl);

            const workletNode = new AudioWorkletNode(audioContext, 'pcm-processor');
            processorRef.current = workletNode;

            workletNode.port.onmessage = (event: MessageEvent) => {
                if (clientRef.current) {
                    // Copy to ensure a plain ArrayBuffer (not SharedArrayBuffer)
                    clientRef.current.sendAudio(new Float32Array(event.data as Float32Array));
                }
            };

            source.connect(workletNode);
            workletNode.connect(audioContext.destination);


            // const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);
            // scriptProcessor.onaudioprocess = (event) => {
            //     if (clientRef.current) {
            //         const inputData = event.inputBuffer.getChannelData(0);
            //         clientRef.current.sendAudio(inputData);
            //     }
            // };
            // scriptProcessorRef.current = scriptProcessor;
            // source.connect(scriptProcessor);
            // scriptProcessor.connect(audioContext.destination);
        } catch (err: any) {
            setError(err.message || "Failed to start recording");
            cleanup();
            setIsRecording(false);
        }
    }, [cleanup, stopRecording, onProfileCaptured]);

    useEffect(() => {
        return () => { cleanup(); };
    }, [cleanup]);

    const parsedProfiles = useMemo(() => deserializeProfiles(existingProfile), [existingProfile]);

    return (
        <div className="flex flex-col gap-2">



            {isPopup && <p className="hsub">{appState.t("speaker.mustRecord")} <strong className="italic">{displayLabel}</strong></p>}

            <div className="flex flex-row items-center gap-1">
                <button
                    className={`btn ${isRecording ? 'btn-danger' : 'btn-record'}`}
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={micPermission === false}
                >
                    {micPermission === false
                        ? appState.t("speaker.noMic")
                        : isRecording
                            ? appState.t("speaker.stopProcess")
                            : appState.t("speaker.record")}
                </button>

                {isRecording && (
                    <div className="level-meter">
                        <div
                            className="level-meter-fill"
                            style={{ width: `${Math.min(100, Math.max(0, audioLevel * 2))}%` }}
                        />
                    </div>
                )}

                <span className={`recorder-status ${isRecording ? 'recorder-status-live' : ''}`}>
                    {statusText}
                </span>
            </div>

            {transcript && (
                <div className="transcript-quote mt-2">
                    "{transcript}"
                </div>
            )}

            {error && <p className="error-text">{error}</p>}
        </div>
    );
});
